import { useSelector } from "react-redux";

// Constants and Utils
import {
  roundUpNearestInteger,
  toFixedIfDecimalNumber,
  toStringWithRoundUp,
} from "../../../common-utils/number-utils/NumberUtils";
import { constructTotalImpAndCost } from "../../utils/campaignPlanningUtil";

// Page Functions
function calculateOtsSplitAndCost(
  selectedRoads,
  tgSpecificOts,
  dayCount,
  tgPriceFactor,
  genPriceFactor
) {
  const roadStretchOtsMap = {};
  Object.keys(selectedRoads).forEach((stretchId) => {
    const selectedStretchInfo = selectedRoads[stretchId];
    const { otsAvg, otsLitAvg, avgMOP } = selectedStretchInfo || {};
    const tgStretchInfo = tgSpecificOts[stretchId];
    const { targetOts, targetOtsLit, targetGroupId } = tgStretchInfo || {};

    const requiredOtsBean = {
      avgMOP,
      otsAvg,
      otsLitAvg,
      targetGroupId,
      targetOts,
      targetOtsLit,
    };

    roadStretchOtsMap[stretchId] = requiredOtsBean;
  });

  return constructTotalImpAndCost(
    selectedRoads,
    roadStretchOtsMap,
    dayCount,
    tgPriceFactor,
    genPriceFactor
  );
}

function calculateAccImpAndCost(
  segmentOtsSplitAndCost,
  stretchOtsSplitAndCost
) {
  const totalOtsSplitAndCost = {
    genericImpressions: 0,
    specificImpressions: 0,
    specificCost: 0,
    genericCost: 0,
  };

  // Segment ots and cost calculation
  //---------------------------------------
  totalOtsSplitAndCost.specificImpressions =
    totalOtsSplitAndCost.specificImpressions +
    segmentOtsSplitAndCost.specificImpressions;

  totalOtsSplitAndCost.genericImpressions =
    totalOtsSplitAndCost.genericImpressions +
    segmentOtsSplitAndCost.genericImpressions;

  totalOtsSplitAndCost.specificCost =
    totalOtsSplitAndCost.specificCost + segmentOtsSplitAndCost.specificCost;
  totalOtsSplitAndCost.genericCost =
    totalOtsSplitAndCost.genericCost + segmentOtsSplitAndCost.genericCost;

  // Stretch ots and cost calculation
  //---------------------------------------
  totalOtsSplitAndCost.specificImpressions =
    totalOtsSplitAndCost.specificImpressions +
    stretchOtsSplitAndCost.specificImpressions;

  totalOtsSplitAndCost.genericImpressions =
    totalOtsSplitAndCost.genericImpressions +
    stretchOtsSplitAndCost.genericImpressions;

  totalOtsSplitAndCost.specificCost =
    totalOtsSplitAndCost.specificCost + stretchOtsSplitAndCost.specificCost;
  totalOtsSplitAndCost.genericCost =
    totalOtsSplitAndCost.genericCost + stretchOtsSplitAndCost.genericCost;

  return totalOtsSplitAndCost;
}

// get Cost Per Impressions Of Stretches
function getCostPerImpressionsOfStretches(
  stretchesMap = {},
  tgId,
  tgSpecificOts,
  dayCount
) {
  const roadStretches = Object.values(stretchesMap);

  let totalMOPSum = 0;
  let totalTgImpressionsSum = 0;
  let totalGenericImpressionsSum = 0;

  roadStretches.forEach((stretchInfo) => {
    const {
      avgMOP = 0,
      mediaCount = 0,
      id: stretchId,
      otsAvg = 0,
      otsLitAvg = 0,
    } = stretchInfo || {};
    const { targetOts = 0, targetOtsLit = 0 } = tgSpecificOts[stretchId] || {};

    // MOP
    const roundUpAvgMOP = avgMOP === 0 ? 0 : roundUpNearestInteger(avgMOP);
    const totalMOP = roundUpAvgMOP * mediaCount;

    // TG Impressions
    const tgImpressions = targetOtsLit ?? targetOts;
    const formattedTgImpressions =
      tgImpressions === 0 ? 0 : roundUpNearestInteger(tgImpressions * dayCount);

    // Generic Impressions
    const genericImpressions = otsLitAvg ?? otsAvg;
    const formattedGenericImpressions =
      genericImpressions === 0
        ? 0
        : roundUpNearestInteger(genericImpressions * dayCount);

    totalMOPSum += totalMOP;
    totalTgImpressionsSum += formattedTgImpressions;
    totalGenericImpressionsSum += formattedGenericImpressions;
  });

  const finalTotalImpressions = tgId
    ? totalTgImpressionsSum
    : totalGenericImpressionsSum;

  // avoid divide by zero
  if (finalTotalImpressions === 0) {
    return 0;
  }

  // CostPerImpressions
  // Formula will vary according to TG applied
  // CPI = Sum of site MOP's of the selected Stretches / Sum of TG Imp. of selected Stretches
  // CPI = Sum of site MOP's of the selected Stretches / Sum of Generic Imp. of selected Stretches
  return totalMOPSum / finalTotalImpressions;
}

/**
 * Cost And Ots Section
 */
export default function CostAndOtsSection() {
  const tgId = useSelector((state) => state.tgSpecificOts.tgId);

  const originalCityStretchesMap = useSelector(
    (state) => state.planningRoadStretches.originalCityStretchesMap
  );

  // TG Specific impressions
  const tgSpecificOts = useSelector(
    (state) => state.tgSpecificOts.tgSpecificOts
  );

  //  impressions of Stretch
  const selectedStretches = useSelector(
    (state) => state.planningRoadStretches.selectedStretches
  );

  const selectedStretchCount = Object.keys(selectedStretches).length;

  const genPriceFactor = useSelector((state) => state.campaignPlanning.genCpm);
  const tgPriceFactor = useSelector((state) => state.campaignPlanning.tgCpm);

  // Duration
  const duration = useSelector(
    (state) => state.campaignPlanning.durationInDays
  );

  const segmentOtsSplitAndCost = {
    genericImpressions: 0,
    specificImpressions: 0,
    specificCost: 0,
    genericCost: 0,
  };

  // For Road-Stretches..
  const stretchOtsSplitAndCost = calculateOtsSplitAndCost(
    selectedStretches,
    tgId && Object.keys(tgSpecificOts).length > 0
      ? tgSpecificOts
      : originalCityStretchesMap,
    duration,
    tgPriceFactor,
    genPriceFactor
  );
  const { avgMOP = 0 } = stretchOtsSplitAndCost;

  const totalAvgMop =
    selectedStretchCount > 0 ? avgMOP / selectedStretchCount : 0;

  const { genericImpressions, specificImpressions } = calculateAccImpAndCost(
    segmentOtsSplitAndCost,
    stretchOtsSplitAndCost
  );

  // total cost per impressions of selectedStretches
  const totalCostPerImpressions = getCostPerImpressionsOfStretches(
    selectedStretches,
    tgId,
    tgSpecificOts,
    duration
  );

  return (
    <>
      <hr className="divider my-0"></hr>
      <div className="d-flex flex-column justify-content-between p-3">
        <div className="row px-3">
          <p className="border py-2 col-7 mb-0">
            <b>Total Estimated Imp of Selected Road Stretches: </b>
            {genericImpressions !== 0
              ? toStringWithRoundUp(genericImpressions)
              : "0"}
          </p>
          <p className="border py-2 col-5 mb-0">
            <b>Budget as per MOP: </b>
            {totalAvgMop !== 0 ? toStringWithRoundUp(totalAvgMop) : "0"}
          </p>
        </div>
        <div className="row px-3">
          <p className="border py-2 col-7 mb-0">
            <b>Total Estimated TG Imp of Selected Road Stretches: </b>
            {specificImpressions !== 0
              ? toStringWithRoundUp(specificImpressions)
              : "0"}
          </p>
          <p className="border py-2 col-5 mb-0">
            <b>Cost per Imp: </b>
            <span>{toFixedIfDecimalNumber(totalCostPerImpressions)}</span>
          </p>
        </div>
      </div>
    </>
  );
}
